import React, { useEffect, useMemo, useState } from 'react';
import { Mail } from 'lucide-react';
import { CatalogueShell } from './CatalogueShell';
import { CATEGORY_LINKS } from './CatalogueNav';
import { Gallery, VariantSelector, SpecTable, DocumentList, FamilyStrip } from './ProductParts';
import RichTextContent from '../RichTextContent';

const slugify = (s) => s.toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/#/g, ' ').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

export const VariantProductDetail = ({ brandId, data }) => {
  const { product, familyProducts } = data;
  const [variantIdx, setVariantIdx] = useState(0);
  const [imgIdx, setImgIdx] = useState(0);
  const variant = product.variants[variantIdx] || {};
  const images = useMemo(() => {
    const all = product.images?.length ? product.images : [product.mainImage].filter(Boolean);
    return variant.image ? [variant.image, ...all.filter((i) => i !== variant.image)] : all;
  }, [product, variant.image]);

  useEffect(() => { setVariantIdx(0); }, [product.slug]);
  useEffect(() => { setImgIdx(0); }, [variantIdx, product.slug]);

  const cat = CATEGORY_LINKS.find((c) => c.slug === product.categories?.[0]);
  const mailto = `mailto:info@meshlighting.co.uk?subject=${encodeURIComponent(`Konsthantverk enquiry – ${product.name} ${variant.code || product.code}`)}&body=${encodeURIComponent(
    `I would like to enquire about:\n\nProduct: ${product.name} ${product.productType} (${variant.code || product.code})\nFinish: ${variant.finish || '-'}\nPage: ${window.location.href}\n\nPlease provide pricing and lead time.`
  )}`;

  return (
    <CatalogueShell
      brandId={brandId}
      crumbs={[
        { label: 'Lighting', to: `/brand/${brandId}/lighting` },
        ...(cat ? [{ label: cat.label, to: `/brand/${brandId}/lighting/${cat.slug}` }] : []),
        { label: `${product.name} ${product.code}` },
      ]}
      testId="variant-product-page"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
        <div className="lg:col-span-7">
          <Gallery images={images} alt={product.title} index={imgIdx} setIndex={setImgIdx} />
        </div>
        <div className="lg:col-span-5">
          <p className="text-xs uppercase tracking-widest text-gray-400 mb-2" data-testid="product-family-type">
            {product.family} family · {product.categories?.map((c) => CATEGORY_LINKS.find((x) => x.slug === c)?.label).filter(Boolean).join(', ')}
          </p>
          <h1 className="text-4xl sm:text-5xl font-light text-gray-900 mb-2" data-testid="product-name">{product.name}</h1>
          <p className="text-gray-500 mb-8"><span data-testid="product-type">{product.productType}</span> · <span className="font-mono" data-testid="product-code">{product.code}</span></p>
          {product.variants.length > 0 && <VariantSelector variants={product.variants} selected={variantIdx} onSelect={setVariantIdx} />}
          <a href={mailto} className="inline-flex items-center w-full justify-center px-6 py-4 bg-gray-900 text-white font-medium rounded-sm hover:bg-gray-800 transition-colors" data-testid="enquiry-button">
            <Mail className="h-4 w-4 mr-2" /> Enquire about {variant.code || product.code}
          </a>
        </div>
      </div>

      <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7"><SpecTable variant={variant} /></div>
        <div className="lg:col-span-5 space-y-10">
          {product.description && (
            <section data-testid="description-section">
              <h2 className="text-xl font-medium text-gray-900 mb-4">About {product.name}</h2>
              <RichTextContent content={product.description} className="text-gray-600 leading-relaxed text-sm [&_p]:mb-3" />
            </section>
          )}
          <DocumentList documents={product.documents} />
        </div>
      </div>

      <FamilyStrip brandId={brandId} family={product.family} familySlug={slugify(product.family)} products={familyProducts} />
    </CatalogueShell>
  );
};
